import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import {
    CalendarTodayOutlined,
    CheckCircleOutlined,
    DateRangeOutlined,
    FlagOutlined,
    HighlightOffOutlined,
    HowToRegOutlined,
    ReportProblemOutlined,
    TodayOutlined
} from '@material-ui/icons';
import PageTitle from '../../shared/pagetitle/PageTitle.js';
import Separator from '../../shared/separator/Separator.js';
import CardMetrica from './CardMetrica.jsx';
import PainelLista from './PainelLista.jsx';
import GraficoEvolucaoInscricoes from './GraficoEvolucaoInscricoes.jsx';
import PainelDocumentos from './PainelDocumentos.jsx';
import PainelConcorrencia from './PainelConcorrencia.jsx';
import {classesDaCategoria, useEstilosAcompanhamento} from './estilosAcompanhamento.js';
import {montarCardsSituacao, montarItensPeriodo, preencherDias} from './acompanhamentoEstatistico.js';
import useSWRFetch from '../../../hooks/useSWRFetch.js';
import {URL_ACOMPANHAMENTO_ESTATISTICAS} from '../../../constants/index.js';

const SWR_CONFIG = {refreshInterval: 60000, revalidateOnFocus: false};
const PARAMS = {};

/* Cor do status do processo: inscrição aberta em verde, fase de agendamento em âmbar, demais neutros. */
const CATEGORIAS_STATUS = {
    INSCRICOES_NAO_ABERTAS: 'info',
    PERIODO_INSCRICOES: 'sucesso',
    PERIODO_AGENDAMENTO: 'pendente',
    CONCLUIDO: 'total'
};

const ICONES_SITUACAO = {
    H: CheckCircleOutlined,
    P: ReportProblemOutlined,
    I: HighlightOffOutlined
};

const ICONES_PERIODO = {
    DIA: TodayOutlined,
    SEMANA: DateRangeOutlined,
    MES: CalendarTodayOutlined
};

/* Separador vertical entre cards; some abaixo de lg, onde o kpiGrid quebra linha; de lg para cima o kpiGrid não quebra linha. O Separator ignora className, por isso o wrapper. */
function SeparadorKpi() {
    const classes = useEstilosAcompanhamento();
    return (
        <div className={classes.separadorKpi}>
            <Separator noCenterText noHorizontalMargin orientation='vertical'/>
        </div>
    );
}

/*
 * Conteúdo da Visão Geral do processo de seleção escolhido, sem Paper (o Paper é da página). Programa interno:
 * cards de status, inscritos e situações H/P/I e, abaixo, os quadros Evolução das Inscrições, Inscrições no
 * Período, Documentos e Pendências e Concorrência por Curso (cada quadro com PageTitle, separados só pelo espaçamento do
 * Grid). Programa do governo: só os cards de status e selecionados.
 */
export default function PainelEstatisticas({idProcessoSeletivo}) {
    const classes = useEstilosAcompanhamento();
    const estatisticas = useSWRFetch(URL_ACOMPANHAMENTO_ESTATISTICAS(idProcessoSeletivo), PARAMS, SWR_CONFIG);
    const inscricoesPorDia = estatisticas ? estatisticas.inscricoesPorDia : undefined;
    const situacoes = estatisticas ? estatisticas.situacoes : undefined;
    const inscricoes = estatisticas ? estatisticas.inscricoes : undefined;
    const dias = useMemo(() => preencherDias(inscricoesPorDia), [inscricoesPorDia]);
    const cardsSituacao = useMemo(() => montarCardsSituacao(situacoes), [situacoes]);
    const itensPeriodo = useMemo(
        () => montarItensPeriodo(inscricoes).map(item => ({...item, Icon: ICONES_PERIODO[item.chave]})),
        [inscricoes]
    );

    if (!estatisticas) {
        return <Typography className={classes.semDadosPainel}>Nenhum dado disponível.</Typography>;
    }

    const cards = (
        <div className={classes.kpiGrid}>
            <CardMetrica
                titulo='Status do processo'
                valor={estatisticas.descricaoStatus}
                {...classesDaCategoria(classes, CATEGORIAS_STATUS[estatisticas.status] || 'total')}
                Icon={FlagOutlined}/>
            {estatisticas.programaInterno ? (
                <>
                    <SeparadorKpi/>
                    <CardMetrica
                        titulo='Inscritos'
                        valor={estatisticas.quantidadeInscritos ?? 0}
                        {...classesDaCategoria(classes, 'total')}
                        Icon={HowToRegOutlined}/>
                    {cardsSituacao.map(item => (
                        <React.Fragment key={item.codigo}>
                            <SeparadorKpi/>
                            <CardMetrica
                                titulo={item.titulo}
                                valor={item.quantidade}
                                valorSecundario={item.valorSecundario}
                                {...classesDaCategoria(classes, item.categoria)}
                                Icon={ICONES_SITUACAO[item.codigo]}/>
                        </React.Fragment>
                    ))}
                </>
            ) : (
                <CardMetrica
                    titulo='Selecionados'
                    valor={estatisticas.quantidadeSelecionados ?? 0}
                    {...classesDaCategoria(classes, 'sucesso')}
                    Icon={CheckCircleOutlined}/>
            )}
        </div>
    );

    if (!estatisticas.programaInterno) {
        return cards;
    }

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                {cards}
            </Grid>
            <Grid item xs={12} lg={8}>
                <PageTitle title='Evolução das Inscrições'/>
                <GraficoEvolucaoInscricoes dias={dias}/>
            </Grid>
            <Grid item xs={12} lg={4}>
                <PageTitle title='Inscrições no Período'/>
                <PainelLista itens={itensPeriodo}/>
            </Grid>
            <Grid item xs={12} lg={4}>
                <PainelDocumentos idProcessoSeletivo={idProcessoSeletivo}/>
            </Grid>
            <Grid item xs={12} lg={8}>
                <PainelConcorrencia idProcessoSeletivo={idProcessoSeletivo}/>
            </Grid>
        </Grid>
    );
}

PainelEstatisticas.propTypes = {
    idProcessoSeletivo: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
};
